import React, { useCallback, useEffect, useRef } from "react";
import { ScrollView, AppState, BackHandler } from "react-native";
import {
  Box,
  MainText,
  MySafeAreaView,
  PressableOpacity,
} from "../../components/common";
import { batch, useDispatch, useSelector } from "react-redux";
import {
  modifyUser,
  updateUserWithoutResponseRequest,
} from "@microdoing/shared/services/user/actions";
import { getUserId, getUser } from "@microdoing/shared/services/user/selectors";
import { changeUserInfo } from "@microdoing/shared/services/profileSettings/actions";
import { logout } from "@microdoing/shared/services/login/actions";
import i18n from "@microdoing/shared/i18n";
import theme from "app/Theme/Theme";
import {
  getBio,
  getBirthDate,
  getDisc,
  getFirstName,
  getJobDescription,
  getJobDuration,
  getLanguage,
  getLastName,
  getManager,
  getMbti,
} from "@microdoing/shared/services/profileSettings/selectors";
import { useIsFocused, useNavigation } from "@react-navigation/native";
import Profile from "./Profile";
import ProfileLanguage from "./ProfileLanguage";
import ProfileOthers from "./ProfileOthers";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import WebDesktopView from "app/components/common/Web/WebDesktopView";
import { breadCrumbsProfileSettingsScreen } from "app/navigation/breadcrumb/BreadcrumbPaths";
import SettingsHeader from "./SettingsHeader";
import ArrowLeft from "app/components/Icons/Arrow/ArrowLeft";
import normalize from "app/utils/normalize";
import { getUserGoalNumber } from "@microdoing/shared/services/user/selectors";
import SafeAreaColor from "../Notification/SafeAreaColor";

const ProfileSettings = () => {
  const firstName = useSelector(getFirstName);
  const lastName = useSelector(getLastName);
  const bio = useSelector(getBio);
  const jobDuration = useSelector(getJobDuration);
  const settingLanguage = useSelector(getLanguage);
  const manager = useSelector(getManager); // !!! je crois pas utilisé
  const jobDescription = useSelector(getJobDescription);
  const birthDate = useSelector(getBirthDate); // !!! je crois pas utilisé
  const mbti = useSelector(getMbti);
  const disc = useSelector(getDisc);
  const user = useSelector(getUser);
  const goalNumber = useSelector(getUserGoalNumber);
  const userId = useSelector(getUserId);

  const dispatch = useDispatch();
  const navigation = useNavigation();

  const isFocused = useIsFocused();

  const onSubmitChange = useCallback(
    (shouldUpdateLocally = true) => {
      let jobDurationTemp;

      if (!jobDuration) {
        jobDurationTemp = 0;
      } else {
        jobDurationTemp = parseInt(jobDuration, 10);
      }

      batch(() => {
        dispatch(
          updateUserWithoutResponseRequest({
            userId,
            bio,
            firstName,
            lastName,
            jobDurationTemp,
            manager,
            jobDescription,
            birthDate,
            mbti,
            disc,
            language: settingLanguage,
            settingLanguage,
            goalNumber,
          }),
        );
        // useful when you logout and you want to keep you store clean
        if (shouldUpdateLocally) {
          dispatch(
            modifyUser({
              bio,
              firstName,
              lastName,
              jobDurationTemp,
              manager,
              jobDescription,
              birthDate,
              mbti,
              disc,
              language: settingLanguage,
              settingLanguage,
              goalNumber,
            }),
          );
        }
      });

      return false;
    },
    [
      bio,
      firstName,
      lastName,
      manager,
      jobDescription,
      birthDate,
      mbti,
      disc,
      settingLanguage,
      goalNumber,
    ],
  );

  // Mutable ref pattern to avoid full rerender of event listener and keep the latest version when updating

  useEffect(() => {
    if (isWebDesktop) {
      window?.addEventListener("beforeunload", ev => {
        return onSubmitChange();
      });
      return () => window?.removeEventListener("beforeunload", ev => {});
    }
  }, [onSubmitChange]);

  const onBackScreen = useCallback(() => {
    navigation.goBack();
    onSubmitChange();
  }, [onSubmitChange]);

  useEffect(() => {
    const appStateSubscription = AppState.addEventListener(
      "change",
      async nextAppState => {
        if (nextAppState !== "active") {
          onSubmitChange(false);
        }
      },
    );

    return () => {
      return appStateSubscription.remove();
    };
  }, [onSubmitChange]);

  useEffect(() => {
    if (isWebDesktop) {
      // prevent submitting false info when disconnecting - firstName should always be here
      if (!isFocused && !!firstName && userId) {
        onSubmitChange();
      }
    } else {
      if (!isFocused && !!firstName && !!userId) {
        onSubmitChange();
      }
    }
  }, [isFocused, firstName, userId, onSubmitChange]);

  useEffect(() => {
    let backHandlerSubscription: any = { remove: () => null };
    let unsubscribeBack: any = () => null;
    if (!isWebDesktop) {
      backHandlerSubscription = BackHandler.addEventListener(
        "hardwareBackPress",
        onSubmitChange,
      );

      unsubscribeBack = navigation.addListener("beforeRemove", e => {
        //clear setInterval here and go back
        onSubmitChange();
      });
    }

    return () => {
      backHandlerSubscription.remove();
      unsubscribeBack();
    };
  }, [onSubmitChange]);

  useEffect(() => {
    const {
      jobDescription,
      firstName,
      lastName,
      bio,
      birthDate,
      manager = false,
      mbti,
      language,
      jobDuration,
      disc = "",
      goalNumber,
    } = user;

    let cleanJobDuration = "0";

    if (jobDuration) {
      cleanJobDuration = jobDuration.toString();
    }

    dispatch(
      changeUserInfo({
        settingLanguage: language,
        jobDescription,
        firstName,
        lastName,
        bio,
        birthDate,
        manager,
        mbti,
        jobDuration: cleanJobDuration,
        disc,
        goalNumber,
      }),
    );
  }, []);

  const handleLogout = () => {
    dispatch(logout({ userId }));
  };

  if (isWebDesktop) {
    return (
      <WebDesktopView breadcrumbsPath={breadCrumbsProfileSettingsScreen}>
        <ScrollView
          contentContainerStyle={styles.scrollViewDesktop}
          style={{ height: "100%", flex: 1 }}
          testID="scrollView"
        >
          <Box flex={1}>
            <Profile />
          </Box>
          <Box flex={1}>
            <ProfileLanguage />
            <ProfileOthers />
            <Box
              width="100%"
              marginTop="xs"
              alignItems="center"
              justifyContent="flex-start"
            >
              <PressableOpacity onPress={handleLogout}>
                <MainText variant="l3-medium" color="neutral.500">
                  {i18n.t("logout")}
                </MainText>
              </PressableOpacity>
            </Box>
          </Box>
        </ScrollView>
      </WebDesktopView>
    );
  }

  return (
    <MySafeAreaView flex={1} backgroundColor="neutral.white">
      <SettingsHeader
        callbackOnPressLeft={onBackScreen}
        title={i18n.t("profile-settings")}
        leftIcon={
          <ArrowLeft
            color={theme.colors["neutral.900"]}
            height={normalize(30)}
            width={normalize(30)}
          />
        }
      />
      <ScrollView
        contentContainerStyle={styles.scrollView}
        showsVerticalScrollIndicator={false}
        testID="scrollView"
      >
        <Profile />
        <ProfileLanguage />
        <ProfileOthers />
        <Box
          width="100%"
          marginTop="xs"
          alignItems="center"
          justifyContent="flex-start"
        >
          <PressableOpacity onPress={handleLogout}>
            <MainText variant="l4-medium" color="neutral.500">
              {i18n.t("logout")}
            </MainText>
          </PressableOpacity>
        </Box>
      </ScrollView>
    </MySafeAreaView>
  );
};

const styles = {
  scrollView: {
    paddingBottom: 80,
  },
  scrollViewDesktop: {
    paddingTop: !isWebDesktop && 60,
    width: "100%",
    justifyContent: "center",
    alignItems: "flex-start",
    display: "flex",
    flexDirection: "row",
    columnGap: theme.spacing.xm,
  },
};

export default ProfileSettings;
